/**
 * Deutschlandatlas Import Page
 *
 * Auswahl-Import-Tool (analog GenesisImport.tsx / InkarImport.tsx):
 * XLSX-Datei waehlen oder hochladen, Datei wird automatisch gescannt,
 * gewuenschte Indikatoren (gruppiert nach Ebene) auswaehlen, Ziel-GV100-Edition
 * waehlen, Import starten und Job-Status pollen.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Database,
  FileSpreadsheet,
  Loader2,
  Play,
  Sparkles,
  Upload,
} from 'lucide-react'
import {
  useDaFiles,
  useDaUpload,
  useDaScan,
  useDaExecuteImport,
  useDaImportStatus,
} from '../hooks/deutschlandatlasQueries'
import { useEditions } from '../hooks/queries'
import type { IndicatorDescriptor } from '../services/deutschlandatlasApi'

// ============================================================================
// Konstanten
// ============================================================================

const LEVEL_ORDER: IndicatorDescriptor['level'][] = ['county', 'municipality', 'municipal_association']

const LEVEL_LABELS: Record<IndicatorDescriptor['level'], string> = {
  county: 'Kreise',
  municipality: 'Gemeinden',
  municipal_association: 'Gemeindeverbände',
}

const VALUE_KIND_LABELS: Record<IndicatorDescriptor['value_kind'], string> = {
  class: 'Klasse',
  numeric: 'Zahl',
}

// kuerzel der "Marktsituation"-Vorauswahl (Wohnungsmarkt-Kennzahlen)
const MARKTSITUATION_KUERZEL = ['preis_miet', 'preis_baul', 'wohn_leer']

function indicatorKey(indicator: Pick<IndicatorDescriptor, 'sheet' | 'kuerzel'>): string {
  return `${indicator.sheet}::${indicator.kuerzel}`
}

// ============================================================================
// Main Component
// ============================================================================

export function DeutschlandatlasImport() {
  const { data: filesData, isLoading: filesLoading } = useDaFiles()
  const { data: editionsData, isLoading: editionsLoading } = useEditions('GV100')
  const editions = editionsData?.editions ?? []
  const files = useMemo(() => filesData?.files ?? [], [filesData])

  const [selectedFile, setSelectedFile] = useState<string | null>(null)
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [editionId, setEditionId] = useState<number | ''>('')
  const [jobId, setJobId] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const upload = useDaUpload()
  const { data: scanData, isLoading: scanLoading, isError: scanError } = useDaScan(selectedFile)
  const execute = useDaExecuteImport()
  const { data: status } = useDaImportStatus(jobId)

  const indicators = useMemo(() => scanData?.indicators ?? [], [scanData])

  // Beim Wechsel der Datei die Auswahl zuruecksetzen
  useEffect(() => {
    setSelectedKeys(new Set())
  }, [selectedFile])

  // Wenn Dateien geladen sind und noch keine ausgewaehlt ist, erste Datei vorauswaehlen
  useEffect(() => {
    if (!selectedFile && files.length > 0) {
      setSelectedFile(files[0])
    }
  }, [files, selectedFile])

  const groupedIndicators = useMemo(() => {
    const groups = new Map<IndicatorDescriptor['level'], IndicatorDescriptor[]>()
    for (const indicator of indicators) {
      const list = groups.get(indicator.level) ?? []
      list.push(indicator)
      groups.set(indicator.level, list)
    }
    return groups
  }, [indicators])

  const toggleIndicator = (indicator: IndicatorDescriptor) => {
    const key = indicatorKey(indicator)
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const toggleGroup = (level: IndicatorDescriptor['level'], checked: boolean) => {
    const groupIndicators = groupedIndicators.get(level) ?? []
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      for (const indicator of groupIndicators) {
        const key = indicatorKey(indicator)
        if (checked) {
          next.add(key)
        } else {
          next.delete(key)
        }
      }
      return next
    })
  }

  const selectMarktsituation = () => {
    const matches = indicators.filter((indicator) => MARKTSITUATION_KUERZEL.includes(indicator.kuerzel))
    setSelectedKeys(new Set(matches.map(indicatorKey)))
  }

  const handleUpload = async (file: File) => {
    try {
      const res = await upload.mutateAsync(file)
      setSelectedFile(res.filename)
    } catch (error) {
      console.error('Deutschlandatlas-Upload fehlgeschlagen:', error)
    }
  }

  const selectedIndicators = indicators.filter((indicator) => selectedKeys.has(indicatorKey(indicator)))
  const canExecute =
    selectedFile !== null && editionId !== '' && selectedIndicators.length > 0 && !execute.isPending

  const start = async () => {
    if (!selectedFile || editionId === '' || selectedIndicators.length === 0) return
    try {
      const res = await execute.mutateAsync({
        file: selectedFile,
        selections: selectedIndicators.map((indicator) => ({
          sheet: indicator.sheet,
          kuerzel: indicator.kuerzel,
        })),
        target_edition_id: Number(editionId),
      })
      setJobId(res.job_id)
    } catch (error) {
      console.error('Deutschlandatlas-Import fehlgeschlagen:', error)
    }
  }

  const reset = () => {
    setJobId(null)
    setEditionId('')
    setSelectedKeys(new Set())
  }

  const isRunning = status?.status === 'pending' || status?.status === 'queued' || status?.status === 'running'
  const isCompleted = status?.status === 'completed'
  const isFailed = status?.status === 'failed'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Deutschlandatlas</h1>
        <p className="text-gray-500 mt-1">
          Indikatoren aus dem Deutschlandatlas (BBSR) auswaehlen und importieren
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6 max-w-3xl">
        {/* Datei-Auswahl */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <FileSpreadsheet className="w-4 h-4 inline mr-1" />
            Datei
          </label>
          <div className="flex items-center gap-3">
            <select
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
              value={selectedFile ?? ''}
              disabled={filesLoading || isRunning || files.length === 0}
              onChange={(e) => setSelectedFile(e.target.value || null)}
            >
              {files.length === 0 && <option value="">– keine Datei vorhanden –</option>}
              {files.map((file) => (
                <option key={file} value={file}>
                  {file}
                </option>
              ))}
            </select>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) handleUpload(file)
                e.target.value = ''
              }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={upload.isPending || isRunning}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
            >
              {upload.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              Hochladen
            </button>
          </div>

          {!filesLoading && files.length === 0 && (
            <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>XLSX hochladen</span>
            </div>
          )}

          {upload.isError && (
            <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 mt-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>Upload fehlgeschlagen: {upload.error.message}</span>
            </div>
          )}
        </div>

        {/* Indikator-Auswahl */}
        {selectedFile && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Indikatoren</label>
              <button
                onClick={selectMarktsituation}
                disabled={scanLoading || indicators.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Marktsituation vorauswählen
              </button>
            </div>

            {scanLoading && (
              <div className="flex items-center gap-2 text-gray-500 py-4">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Datei wird analysiert...</span>
              </div>
            )}

            {scanError && (
              <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>Datei konnte nicht analysiert werden.</span>
              </div>
            )}

            {!scanLoading && !scanError && indicators.length > 0 && (
              <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto divide-y divide-gray-200">
                {LEVEL_ORDER.filter((level) => groupedIndicators.has(level)).map((level) => {
                  const groupIndicators = groupedIndicators.get(level) ?? []
                  const allSelected = groupIndicators.every((indicator) =>
                    selectedKeys.has(indicatorKey(indicator))
                  )
                  return (
                    <div key={level} className="p-3">
                      <label className="flex items-center gap-2 mb-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={(e) => toggleGroup(level, e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm font-semibold text-gray-900">
                          {LEVEL_LABELS[level]}
                        </span>
                        <span className="text-xs text-gray-400">({groupIndicators.length})</span>
                      </label>
                      <div className="space-y-1 pl-1">
                        {groupIndicators.map((indicator) => {
                          const key = indicatorKey(indicator)
                          return (
                            <label
                              key={key}
                              className="flex items-center gap-2 text-sm py-1 px-2 rounded hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selectedKeys.has(key)}
                                onChange={() => toggleIndicator(indicator)}
                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                              />
                              <span className="flex-1 text-gray-800">{indicator.name}</span>
                              <span className="flex items-center gap-1 flex-shrink-0">
                                {indicator.data_year && (
                                  <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                                    {indicator.data_year}
                                  </span>
                                )}
                                <span className="px-1.5 py-0.5 text-xs bg-indigo-50 text-indigo-700 rounded">
                                  {VALUE_KIND_LABELS[indicator.value_kind]}
                                </span>
                                <span className="px-1.5 py-0.5 text-xs font-mono bg-gray-100 text-gray-500 rounded">
                                  {indicator.kuerzel}
                                </span>
                              </span>
                            </label>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {!scanLoading && !scanError && indicators.length > 0 && (
              <p className="text-xs text-gray-500 mt-2">
                {selectedIndicators.length} von {indicators.length} Indikatoren ausgewählt
              </p>
            )}
          </div>
        )}

        {/* Edition Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Database className="w-4 h-4 inline mr-1" />
            Edition (Gebietsstand)
          </label>
          <select
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
            value={editionId}
            disabled={editionsLoading || isRunning}
            onChange={(e) => setEditionId(e.target.value ? Number(e.target.value) : '')}
          >
            <option value="">– Edition wählen –</option>
            {editions.map((e) => (
              <option key={e.id} value={e.id}>
                {e.edition} (Gebietsstand {e.reference_year})
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={start}
          disabled={!canExecute || isRunning}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {execute.isPending || isRunning ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Import läuft...
            </>
          ) : (
            <>
              <Play className="w-4 h-4" />
              Import starten
            </>
          )}
        </button>

        {execute.isError && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>Import konnte nicht gestartet werden: {execute.error.message}</span>
          </div>
        )}

        {/* Status */}
        {status && (
          <div
            className={
              isCompleted
                ? 'bg-green-50 border border-green-200 rounded-lg p-4 space-y-2'
                : isFailed
                ? 'bg-red-50 border border-red-200 rounded-lg p-4 space-y-2'
                : 'bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2'
            }
          >
            <div className="flex items-center gap-2 text-sm font-medium">
              {isCompleted && <CheckCircle2 className="w-4 h-4 text-green-700" />}
              {isFailed && <AlertCircle className="w-4 h-4 text-red-700" />}
              {isRunning && <Loader2 className="w-4 h-4 animate-spin text-blue-700" />}
              <span
                className={
                  isCompleted ? 'text-green-800' : isFailed ? 'text-red-800' : 'text-blue-800'
                }
              >
                Status: {status.status}
              </span>
            </div>

            {status.result && (
              <div className="text-sm text-gray-600 flex flex-wrap gap-x-4 gap-y-1">
                <span>erstellt: {status.result.created}</span>
                <span>aktualisiert: {status.result.updated}</span>
                <span>übersprungen: {status.result.skipped}</span>
              </div>
            )}

            {status.result?.errors && status.result.errors.length > 0 && (
              <div className="flex items-start gap-2 text-sm text-amber-700">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <ul className="list-disc list-inside">
                  {status.result.errors.slice(0, 5).map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </div>
            )}

            {(isCompleted || isFailed) && (
              <button
                onClick={reset}
                className="mt-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Neuer Import
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
